import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { getPlayerId } from '../utils/playerId';
import { reportPageLoadPerformance } from '../utils/usageEvents';
import ShareModal from './ShareModal.jsx';

const fetch = require("node-fetch");

// Mirrors Layout.jsx's own image_blur formula (20 * (1 - progress)) so an
// unsolved thumbnail here looks exactly as blurry as the live game would
// after two correct words - not an arbitrary fade.
function blur_for_two_words_placed(word_count) {
  if (!word_count) {
    return 20;
  }
  const progress = Math.min(2, word_count) / word_count;
  return 20 * (1 - progress);
}

class Profile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'loading', solved: [], unsolved: [], solved_count: 0, total_count: 0, quiz_result: null,
      share_joke_id: null,
    };
  }

  componentDidMount() {
    // a no-op if Layout (or another route) already reported this real
    // page load - only matters when a player's very first load lands
    // directly here, e.g. a bookmarked /profile
    reportPageLoadPerformance(null);
    this.load();
    this.load_quiz_result();
  }

  async load() {
    try {
      const response = await fetch('/api/profile_jokes', { credentials: 'same-origin' });
      if (response.status === 401) {
        this.setState({ status: 'unauthenticated' });
        return;
      }
      const json = await response.json();
      this.setState({
        status: 'loaded',
        solved: json.solved,
        unsolved: json.unsolved,
        solved_count: json.solved_count,
        total_count: json.total_count,
      });
    } catch (error) {
      this.setState({ status: 'error' });
    }
  }

  async load_quiz_result() {
    const player_id = getPlayerId();
    if (!player_id) {
      return;
    }
    try {
      const response = await fetch('/api/humor_quiz/result?player=' + encodeURIComponent(player_id));
      const json = await response.json();
      if (json.taken) {
        this.setState({ quiz_result: json });
      }
    } catch (error) {
      // the quiz prompt just won't show a past result - not worth surfacing an error for
    }
  }

  render() {
    if (this.state.status === 'loading') {
      return <div id="profile_page"><p id="profile_status">Loading…</p></div>;
    }
    if (this.state.status === 'unauthenticated') {
      return (
        <div id="profile_page">
          <Link to="/" id="profile_back_link">← Back to the jokes</Link>
          <p id="profile_status">Log in to see the jokes you've solved.</p>
        </div>
      );
    }
    if (this.state.status === 'error') {
      return <div id="profile_page"><p id="profile_status">Couldn't load your profile right now.</p></div>;
    }
    return (
      <div id="profile_page">
        <Link to="/" id="profile_back_link">← Back to the jokes</Link>
        <h1 id="profile_title">Jokes you've solved</h1>
        <p id="profile_progress">{this.state.solved_count}/{this.state.total_count} jokes solved</p>
        <div id="profile_quiz_card">
          {this.state.quiz_result ? (
            <React.Fragment>
              <p id="profile_quiz_summary">
                Your humor style: <strong>{this.state.quiz_result.dominant_label}</strong>
              </p>
              <Link to="/humor-quiz" id="profile_quiz_link">View results / retake the quiz</Link>
            </React.Fragment>
          ) : (
            <Link to="/humor-quiz" id="profile_quiz_link">Take the humor styles quiz</Link>
          )}
        </div>
        {this.state.solved.length === 0 ? (
          <p id="profile_status">You haven't solved any jokes yet.</p>
        ) : (
          <div className="profile_grid">
            {this.state.solved.map(joke => (
              <Link to={'/solved/' + joke.id} key={joke.id} className="profile_card">
                <button
                  type="button"
                  className="profile_card_share_button"
                  aria-label={'Share "' + joke.setup + '" by email'}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.setState({ share_joke_id: joke.id });
                  }}
                >📤</button>
                {joke.image ? (
                  <img src={joke.image} alt="" className="profile_thumb" />
                ) : (
                  <div className="profile_thumb profile_thumb_placeholder">{joke.punchline}</div>
                )}
                <p className="profile_card_setup">{joke.setup}</p>
              </Link>
            ))}
          </div>
        )}
        {this.state.unsolved.length > 0 && (
          <React.Fragment>
            <h2 id="profile_unsolved_title">Still to solve</h2>
            <div className="profile_grid profile_grid_unsolved">
              {this.state.unsolved.map(joke => (
                <Link to={'/joke/' + joke.id} key={joke.id} className="profile_card profile_card_unsolved">
                  {joke.image ? (
                    <img
                      src={joke.image}
                      alt=""
                      className="profile_thumb"
                      style={{ filter: `blur(${blur_for_two_words_placed(joke.word_count)}px)` }}
                    />
                  ) : (
                    <div className="profile_thumb profile_thumb_placeholder" />
                  )}
                  <p className="profile_card_setup">{joke.setup}</p>
                </Link>
              ))}
            </div>
          </React.Fragment>
        )}
        {this.state.share_joke_id && (
          <ShareModal
            jokeId={this.state.share_joke_id}
            onClose={() => this.setState({ share_joke_id: null })}
          />
        )}
      </div>
    );
  }
}

export default Profile;
